import { createColumnHelper } from "@tanstack/react-table";
import { Order } from "@/types/order";
import { useCalendarContext } from "@/context/calendar";
import { useMemo } from "react";
import { formatDate, formatPrice } from "@/utils/helpers";

const columnHelper = createColumnHelper<Order>();

const useOrders = (orders: Order[]) => {
  const {
    date: { from, to },
  } = useCalendarContext();

  const columns = useMemo(
    () => [
      columnHelper.accessor("display_id", {
        id: "display_id",
        header: "Order ID",
        cell: (info) => <p>{info.getValue()}</p>,
      }),
      columnHelper.accessor("items", {
        id: "order_items",
        header: "Items",
        cell: (info) => {
          const itemsQuantity = info.getValue().length;
          return <p>{itemsQuantity}</p>;
        },
      }),
      columnHelper.accessor(
        (row) => ({
          items: row.items,
          currency: row.currency_code,
        }),
        {
          id: "total_sales",
          header: "Total sales",
          cell: (info) => {
            const { items, currency } = info.getValue();
            const total = items.reduce(
              (sum: number, item: { unit_price: number }) =>
                sum + item.unit_price,
              0
            );
            return (
              <p>{formatPrice(total, currency)}</p>
            );
          },
        }
      ),
      columnHelper.accessor("status", {
        id: "status",
        header: "Status",
        cell: (info) => {
          const status = info.getValue();
          const statusStyles = (() => {
            switch (status) {
              case "pending":
                return "text-yellow-700 border border-yellow-300 bg-yellow-100 rounded-md";
              case "completed":
                return "text-green-700 border border-green-300 bg-green-100 rounded-md";
              case "draft":
                return "text-blue-700 border border-blue-300 bg-blue-100 rounded-md";
              case "archived":
                return "text-gray-500 border border-gray-300 bg-gray-100 rounded-md";
              case "canceled":
                return "text-red-700 border border-red-300 bg-red-100 rounded-md";
              case "requires_action":
                return "text-orange-700 border border-orange-300 bg-orange-100 rounded-md";
              default:
                return "text-gray-500 border border-gray-300 bg-gray-100 rounded-md";
            }
          })();

          return (
            <div
              className={`font-medium ${statusStyles} inline-flex items-start gap-2 px-2 py-1 text-xs font-semibold leading-4`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </div>
          );
        },
      }),
      columnHelper.accessor("created_at", {
        id: "created_at",
        header: "Placed On",
        cell: (info) => {
          return <p>{formatDate(info.getValue())}</p>;
        },
      }),
    ],
    []
  );

  const filteredOrders = useMemo(() => {
    const filtered = orders.filter((order) => {
      const created = new Date(order.created_at).getTime();

      if (!from && !to) {
        return true;
      }

      if (from && !to) {
        const fromStart = new Date(from);
        fromStart.setHours(0, 0, 0, 0);
        const fromEnd = new Date(from);
        fromEnd.setHours(23, 59, 59, 999);
        return created >= fromStart.getTime() && created <= fromEnd.getTime();
      }

      if (from && to) {
        const fromTime = new Date(from);
        fromTime.setHours(0, 0, 0, 0);
        const toTime = new Date(to);
        toTime.setHours(23, 59, 59, 999);
        return created >= fromTime.getTime() && created <= toTime.getTime();
      }

      return true;
    });

    return filtered.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [orders, from, to]);

  return { columns, filteredOrders };
};

export { useOrders };
